import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { useSelectorTyped } from "../../../../common/redux/store";
import { ApplicationBasicInfo, ApplicationDetailsPageActions } from "../redux";
import { ReviewRequestFormSubmission, ReviewRequestType } from "./types";
import { useGetFeatureToggleState } from "../../../../common/hooks/feature-toggles";

export interface ReviewRequestOption {
  key: ReviewRequestType;
  label: string;
}

export const REVIEW_REQUEST_OPTIONS: ReviewRequestOption[] = [
  { key: ReviewRequestType.APPLICATION_INTEGRATION, label: "Application Integration" },
  { key: ReviewRequestType.DATA_ACCESS, label: "Data Access" },
  { key: ReviewRequestType.PRODUCTION_READINESS, label: "Production Readiness" },
  { key: ReviewRequestType.SIGNIFICANT_ENHANCEMENT, label: "Significant Enhancement" },
];

export interface ReviewRequestSubmitPayload {
  applicationId: string;
  applicationName: string;
  applicationCategory: string[];
  reviewType: ReviewRequestType;
  values: Record<string, unknown>;
}

export interface RaiseReviewRequestProps {
  onSubmitReviewRequest?: (payload: ReviewRequestSubmitPayload) => unknown;
  showOktaPreviewNotEnabledModal?: boolean;
}

function isLoggedInUserTeamMember(applicationBasicInfo: ApplicationBasicInfo, loggedInUserEmail: string): boolean {
  return applicationBasicInfo.team.members.some((member) => member.emailId === loggedInUserEmail);
}

export function useRaiseReviewRequestController(props: RaiseReviewRequestProps) {
  const applicationBasicInfo = useSelectorTyped(
    (state) => state.applicationDetailsPage.applicationBasicInfoPEV.value,
  );
  const loggedInUserEmail = useSelectorTyped(
    (state) => state.header.userInfo.email.value,
  );
  const requestApplicationReviewPES = useSelectorTyped((state) => state.applicationDetailsPage.requestApplicationReviewPES);
  const isRaiseReviewRequestEnabled = useGetFeatureToggleState("Neo_Applications_InternalAppReview");
  const dispatch = useDispatch();

  const [activeReviewType, setActiveReviewType] = useState<ReviewRequestType | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showOktaPreviewNotEnabledModal, setShowOktaPreviewNotEnabledModal] = useState(props.showOktaPreviewNotEnabledModal ?? false);

  const openModal = (type: ReviewRequestType) => setActiveReviewType(type);
  const closeModal = () => {
    setActiveReviewType(null);
    dispatch(ApplicationDetailsPageActions.resetRequestApplicationReviewPES());
  };
  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    dispatch(ApplicationDetailsPageActions.resetRequestApplicationReviewPES());
    if (applicationBasicInfo) {
      dispatch(ApplicationDetailsPageActions.fetchActivitiesOfApplication(applicationBasicInfo.id));
    }
  };

  useEffect(() => {
    if (requestApplicationReviewPES.success) {
      setActiveReviewType(null);
      setShowSuccessModal(true);
    }
  }, [dispatch, requestApplicationReviewPES.success]);

  const handleSubmit = (payload: ReviewRequestFormSubmission) => {
    if (!applicationBasicInfo) {
      return;
    }
    const submitPayload: ReviewRequestSubmitPayload = {
      applicationId: applicationBasicInfo.id,
      applicationName: applicationBasicInfo.name,
      applicationCategory: applicationBasicInfo.category ?? [],
      reviewType: payload.reviewType,
      values: payload.values,
    };
    if (props.onSubmitReviewRequest) {
      props.onSubmitReviewRequest(submitPayload);
    }
    else {
      dispatch(ApplicationDetailsPageActions.requestApplicationReview({
        applicationId: applicationBasicInfo.id,
        data: {
          applicationName: submitPayload.applicationName,
          applicationCategory: submitPayload.applicationCategory,
          reviewType: submitPayload.reviewType,
          values: submitPayload.values,
        },
      }));
    }
  };

  return {
    options: REVIEW_REQUEST_OPTIONS,
    activeReviewType,
    showSuccessModal,
    showOktaPreviewNotEnabledModal,
    isSubmitInProgress: () => !!requestApplicationReviewPES.progress,
    defaultPrimaryContact: loggedInUserEmail ?? "",
    shouldRender: () => {
      if (!applicationBasicInfo) {
        return false;
      }
      const canRaiseReviewRequest = applicationBasicInfo.canRaiseReviewRequest
        ?? isLoggedInUserTeamMember(applicationBasicInfo, loggedInUserEmail);
      return isRaiseReviewRequestEnabled
        && applicationBasicInfo.isActive
        && canRaiseReviewRequest;
    },
    onSelectOption: (type: ReviewRequestType) => {
      openModal(type);
    },
    onCancelModal: closeModal,
    onSubmitModal: handleSubmit,
    onCloseSuccessModal: closeSuccessModal,
    onCloseOktaPreviewNotEnabledModal: () => setShowOktaPreviewNotEnabledModal(false),
  };
}
